import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { recruiterAPI } from '../services/api';
import { toast } from '../hooks/use-toast';
import { 
  Briefcase, LogOut, Users, ArrowLeft, Calendar, MessageSquare,
  Loader2, User, Mail, Phone, FileText, Clock, CheckCircle,
  XCircle, Star, ChevronRight, Building, UserMinus, UserPlus
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

const STAGES = [
  { key: 'applied', label: 'Applied', color: 'bg-gray-500', icon: FileText },
  { key: 'screening', label: 'Screening', color: 'bg-yellow-500', icon: Users },
  { key: 'scheduled', label: 'Scheduled', color: 'bg-blue-500', icon: Calendar },
  { key: 'interviewed', label: 'Interview', color: 'bg-purple-500', icon: MessageSquare },
  { key: 'feedback', label: 'Feedback', color: 'bg-indigo-500', icon: Star },
  { key: 'offer', label: 'Offer', color: 'bg-green-500', icon: CheckCircle },
  { key: 'hired', label: 'Hired', color: 'bg-emerald-600', icon: CheckCircle },
  { key: 'rejected', label: 'Rejected', color: 'bg-red-500', icon: XCircle },
];

const ApplicantTracking = () => {
  const navigate = useNavigate();
  /* ===== JWT ACCESS GUARD ===== */
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const role = localStorage.getItem('userRole');
    const allowedRoles = ['recruiter', 'company_hr', 'super_admin'];

    if (!token || !allowedRoles.includes(role)) {
      localStorage.clear();

      if (role === 'recruiter') navigate('/recruiter/login');
      else if (role === 'company_hr') navigate('/hr/login');
      else if (role === 'super_admin') navigate('/admin/login');
      else navigate('/login');

      return;
    }
  }, [navigate]);
  /* ===== END JWT ACCESS GUARD ===== */

  const [loading, setLoading] = useState(true);
  const [applicants, setApplicants] = useState([]);
  const [stageCounts, setStageCounts] = useState({});
  const [selectedStage, setSelectedStage] = useState(null);
  const [selectedApp, setSelectedApp] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Schedule form
  const [scheduleForm, setScheduleForm] = useState({
    scheduledAt: '',
    duration: 60,
    interviewType: 'video',
    location: '',
    interviewerName: '',
    notes: '',
  });

  // Feedback form
  const [feedbackForm, setFeedbackForm] = useState({
    rating: 3,
    technicalSkills: 3,
    communicationSkills: 3,
    cultureFit: 3,
    recommendation: 'hire',
    strengths: '',
    weaknesses: '',
    overallComments: '',
  });

  // Offer form
  const [offerForm, setOfferForm] = useState({
    salary: '',
    joiningDate: '',
    designation: '',
    benefits: '',
    notes: '',
  });

  useEffect(() => {
    const role = localStorage.getItem('userRole');
    if (role !== 'recruiter' && role !== 'super_admin') {
      navigate('/recruiter/login');
      return;
    }
    fetchApplicants();
  }, [navigate, selectedStage]);

  const fetchApplicants = async () => {
    try {
      setLoading(true);
      const response = await recruiterAPI.getAllApplicants(selectedStage);
      setApplicants(response.data);
      setStageCounts(response.stageCounts || {});
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load applicants', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = async (app) => {
    try {
      const response = await recruiterAPI.getApplicationDetails(app.id);
      setSelectedApp(response.data);
      setShowDetailModal(true);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to load details', variant: 'destructive' });
    }
  };

  const handleStageChange = async (appId, newStage) => {
    try {
      await recruiterAPI.updateApplicationStatus(appId, newStage);
      toast({ title: 'Success', description: `Moved to ${newStage}` });
      fetchApplicants();
      if (selectedApp?.id === appId) {
        setSelectedApp({ ...selectedApp, currentStage: newStage });
      }
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to update status', variant: 'destructive' });
    }
  };

  const handleScheduleInterview = async (e) => {
  e.preventDefault();

  if (!scheduleForm.scheduledAt || !scheduleForm.interviewerName) {
    toast({
      title: "Missing information",
      description: "Please select date and interviewer name",
      variant: "destructive",
    });
    return;
  }

  try {
    setSubmitting(true);

    const response = await recruiterAPI.scheduleInterview(
      selectedApp.id,
      scheduleForm
    );

    toast({
      title: "Success",
      description: "Interview scheduled successfully",
    });

    // Update modal instantly
    setSelectedApp({
      ...selectedApp,
      currentStage: "scheduled",
      interviews: [
        ...(selectedApp.interviews || []),
        response.data
      ]
    });

    setShowScheduleModal(false);

    await fetchApplicants();

    setScheduleForm({
      scheduledAt: "",
      duration: 60,
      interviewType: "video",
      location: "",
      interviewerName: "",
      notes: "",
    });

  } catch (error) {
    toast({
      title: "Error",
      description: "Failed to schedule interview",
      variant: "destructive",
    });
  } finally {
    setSubmitting(false);
  }
};

const handleSubmitFeedback = async (e) => {
  e.preventDefault();

  try {
    setSubmitting(true);

    const response = await recruiterAPI.submitFeedback(
      selectedApp.id,
      feedbackForm
    );

    toast({
      title: "Success",
      description: "Feedback submitted successfully",
    });

    // update UI instantly
    setSelectedApp({
      ...selectedApp,
      currentStage: "feedback",
      feedbacks: [
        ...(selectedApp.feedbacks || []),
        response.data
      ]
    });

    setShowFeedbackModal(false);

    await fetchApplicants();

  } catch (error) {
    toast({
      title: "Error",
      description: "Failed to submit feedback",
      variant: "destructive",
    });
  } finally {
    setSubmitting(false);
  }
};

  const handleMakeOffer = async (e) => {
  e.preventDefault();

  try {
    setSubmitting(true);

    const response = await recruiterAPI.makeOffer(
      selectedApp.id,
      offerForm
    );

    toast({
      title: "Success",
      description: "Offer sent successfully",
    });

    // ⭐ Update stage instantly so Hired button appears
    setSelectedApp({
      ...selectedApp,
      currentStage: "offer",
      offerDetails: response.data
    });

    setShowOfferModal(false);

    await fetchApplicants();

  } catch (error) {
    toast({
      title: "Error",
      description: "Failed to send offer",
      variant: "destructive",
    });
  } finally {
    setSubmitting(false);
  }
};

  const handleHire = async (appId) => {
    try {
      await recruiterAPI.hireCandidate(appId);
      toast({ title: 'Success', description: 'Candidate hired!' });
      fetchApplicants();
      setShowDetailModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to hire', variant: 'destructive' });
    }
  };

  const handleReject = async (appId) => {
    if (!window.confirm('Are you sure you want to reject this candidate?')) return;
    try {
      await recruiterAPI.rejectCandidate(appId, 'Application rejected');
      toast({ title: 'Done', description: 'Candidate rejected' });
      fetchApplicants();
      setShowDetailModal(false);
    } catch (error) {
      toast({ title: 'Error', description: 'Failed to reject', variant: 'destructive' });
    }
  };

  const handleWithdraw = async (appId) => {
    if (!window.confirm('Withdraw this candidate from Company HR? They will no longer be visible to HR.')) return;
    try {
      await recruiterAPI.withdrawCandidate(appId);
      toast({ title: 'Success', description: 'Candidate withdrawn from HR pipeline' });
      fetchApplicants();
      setShowDetailModal(false);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to withdraw', variant: 'destructive' });
    }
  };

  const handleReassign = async (appId) => {
    try {
      await recruiterAPI.reassignCandidate(appId);
      toast({ title: 'Success', description: 'Candidate re-assigned to HR pipeline' });
      fetchApplicants();
      setShowDetailModal(false);
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to re-assign', variant: 'destructive' });
    }
  };

  const getStageColor = (stage) => {
    return STAGES.find(s => s.key === stage)?.color || 'bg-gray-500';
  };

  const getStageLabel = (stage) => {
    return STAGES.find(s => s.key === stage)?.label || stage;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-blue-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button 
              variant="ghost" 
              onClick={() => navigate('/recruiter/dashboard')}
              className="text-gray-500 hover:text-gray-900"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-10 h-10 bg-gradient-to-br from-blue-600 to-indigo-600 rounded-lg flex items-center justify-center">
              <Users className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Applicant Tracking</h1>
              <p className="text-sm text-gray-500">{applicants.length} total applicants</p>
            </div>
          </div>
          <Button 
            variant="ghost" 
            onClick={() => { localStorage.clear(); navigate('/recruiter/login'); }}
            className="text-gray-500 hover:text-gray-900 hover:bg-gray-100"
          >
            <LogOut className="w-5 h-5 mr-2" />
            Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Stage Filter Pills */}
        <div className="flex flex-wrap gap-3 mb-8">
          <Button
            variant={selectedStage === null ? "default" : "outline"}
            onClick={() => setSelectedStage(null)}
            className={selectedStage === null ? "bg-blue-600" : "border-gray-300 text-gray-600"}
            data-testid="filter-all"
          >
            All ({Object.values(stageCounts).reduce((a, b) => a + b, 0)})
          </Button>
          {STAGES.slice(0, 6).map(stage => (
            <Button
              key={stage.key}
              variant={selectedStage === stage.key ? "default" : "outline"}
              onClick={() => setSelectedStage(selectedStage === stage.key ? null : stage.key)}
              className={`${selectedStage === stage.key ? stage.color : "border-gray-300 text-gray-600"}`}
              data-testid={`filter-${stage.key}`}
            >
              <stage.icon className="w-4 h-4 mr-2" />
              {stage.label} ({stageCounts[stage.key] || 0})
            </Button>
          ))}
        </div>

        {/* Applicants Grid */}
        {applicants.length === 0 ? (
          <Card className="bg-white border-gray-200 p-8 text-center">
            <Users className="w-12 h-12 text-gray-500 mx-auto mb-4" />
            <p className="text-gray-500">No applicants found</p>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {applicants.map(app => (
              <Card 
                key={app.id} 
                className={`bg-white border-gray-200 p-4 hover:border-blue-300 transition-colors cursor-pointer ${app.isWithdrawn ? 'opacity-60 border-orange-300' : ''}`}
                onClick={() => handleViewDetails(app)}
                data-testid={`applicant-card-${app.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-indigo-500 flex items-center justify-center text-gray-900 font-bold">
                      {app.applicantName?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{app.applicantName || 'Unknown'}</h3>
                      <p className="text-sm text-gray-500">{app.applicantEmail}</p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <span className={`px-2 py-1 rounded text-xs text-gray-900 ${getStageColor(app.currentStage)}`}>
                      {getStageLabel(app.currentStage)}
                    </span>
                    {app.isWithdrawn && (
                      <span className="px-2 py-0.5 rounded text-xs bg-orange-100 text-orange-700 flex items-center gap-1">
                        <UserMinus className="w-3 h-3" /> Withdrawn
                      </span>
                    )}
                  </div>
                </div>
                
                {/* Headline */}
                {app.applicantHeadline && (
                  <p className="text-cyan-400 text-sm mb-2 line-clamp-1">{app.applicantHeadline}</p>
                )}
                
                {/* Job Info */}
                <div className="space-y-1 mb-3">
                  <p className="text-sm text-blue-400 flex items-center gap-1">
                    <Briefcase className="w-4 h-4" />
                    {app.jobTitle}
                  </p>
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Building className="w-4 h-4" />
                    {app.company}
                  </p>
                </div>
                
                {/* Profile Info */}
                <div className="flex flex-wrap gap-2 mb-3">
                  {app.applicantExperience && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      {app.applicantExperience} exp
                    </span>
                  )}
                  {app.applicantLocation && (
                    <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
                      📍 {app.applicantLocation}
                    </span>
                  )}
                  {app.hasResume && (
                    <span className="px-2 py-0.5 bg-green-500/20 rounded text-xs text-green-400">
                      <FileText className="w-3 h-3 inline mr-1" />
                      Resume
                    </span>
                  )}
                </div>
                
                {/* Skills */}
                {app.applicantSkills && app.applicantSkills.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-3">
                    {app.applicantSkills.slice(0, 3).map((skill, idx) => (
                      <span key={idx} className="px-2 py-0.5 bg-blue-500/20 rounded text-xs text-blue-400">
                        {skill}
                      </span>
                    ))}
                    {app.applicantSkills.length > 3 && (
                      <span className="px-2 py-0.5 bg-gray-50 rounded text-xs text-gray-500">
                        +{app.applicantSkills.length - 3}
                      </span>
                    )}
                  </div>
                )}
                
                {/* Footer */}
                <div className="flex items-center justify-between border-t border-gray-200 pt-3 mt-2">
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-3 h-3" />
                      {new Date(app.appliedAt).toLocaleDateString()}
                    </span>
                    {app.applicantTotalApplications > 1 && (
                      <span className="text-yellow-500" title="Total applications by this candidate">
                        {app.applicantTotalApplications} apps
                      </span>
                    )}
                  </div>
                  <ChevronRight className="w-5 h-5 text-gray-500" />
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Detail Modal */}
      <Dialog open={showDetailModal} onOpenChange={setShowDetailModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <User className="w-5 h-5" />
              {selectedApp?.applicantName || 'Applicant Details'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedApp && (
            <div className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2 text-gray-600">
                  <Mail className="w-4 h-4" />
                  {selectedApp.applicantEmail}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Phone className="w-4 h-4" />
                  {selectedApp.applicantPhone || 'N/A'}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Briefcase className="w-4 h-4" />
                  {selectedApp.jobTitle}
                </div>
                <div className="flex items-center gap-2 text-gray-600">
                  <Clock className="w-4 h-4" />
                  {new Date(selectedApp.appliedAt).toLocaleDateString()}
                </div>
              </div>

              {/* Current Stage */}
              <div className="flex items-center gap-3">
                <span className="text-gray-500">Current Stage:</span>
                <span className={`px-3 py-1 rounded text-gray-900 ${getStageColor(selectedApp.currentStage)}`}>
                  {getStageLabel(selectedApp.currentStage)}
                </span>
                {selectedApp.isWithdrawn && (
                  <span className="px-3 py-1 rounded bg-orange-100 text-orange-700 flex items-center gap-1">
                    <UserMinus className="w-4 h-4" /> Withdrawn from HR
                  </span>
                )}
              </div>

              {/* Withdrawn Warning */}
              {selectedApp.isWithdrawn && (
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-orange-700 text-sm">
                  <p className="font-medium">⚠️ This candidate has been withdrawn from Company HR</p>
                  <p className="text-xs mt-1">HR can no longer see or take action on this candidate. Use "Re-assign to HR" to restore access.</p>
                </div>
              )}

              {/* Resume Link */}
              {selectedApp.resumeUrl && (
                <a 
                  href={selectedApp.resumeUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700"
                >
                  <FileText className="w-4 h-4" />
                  View Resume
                </a>
              )}

              {/* Cover Letter */}
              {selectedApp.coverLetter && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Cover Letter</h4>
                  <p className="text-gray-600 text-sm bg-gray-100 p-3 rounded">{selectedApp.coverLetter}</p>
                </div>
              )}

              {/* Stage History */}
              {selectedApp.stageHistory?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">History</h4>
                  <div className="space-y-2">
                    {selectedApp.stageHistory.map((history, idx) => (
                      <div key={idx} className="flex items-center gap-3 text-sm">
                        <span className={`w-2 h-2 rounded-full ${getStageColor(history.stage)}`} />
                        <span className="text-gray-600">{getStageLabel(history.stage)}</span>
                        <span className="text-gray-500">
                          {new Date(history.changedAt).toLocaleDateString()}
                        </span>
                        {history.notes && <span className="text-gray-500 text-xs">- {history.notes}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Interviews */}
              {selectedApp.interviews?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Interviews</h4>
                  {selectedApp.interviews.map((interview, idx) => (
                    <div key={idx} className="bg-gray-100 p-3 rounded mb-2">
                      <div className="flex justify-between">
                        <span className="text-blue-400">{interview.interviewType} Interview</span>
                        <span className="text-gray-500 text-sm">
                          {new Date(interview.scheduledAt).toLocaleString()}
                        </span>
                      </div>
                      {interview.interviewerName && (
                        <p className="text-sm text-gray-500 mt-1">Interviewer: {interview.interviewerName}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Feedbacks */}
              {selectedApp.feedbacks?.length > 0 && (
                <div>
                  <h4 className="text-sm font-medium text-gray-500 mb-2">Feedback</h4>
                  {selectedApp.feedbacks.map((fb, idx) => (
                    <div key={idx} className="bg-gray-100 p-3 rounded mb-2">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-yellow-400">Rating: {fb.rating}/5</span>
                        <span className={`px-2 py-0.5 rounded text-xs ${
                          fb.recommendation === 'strong_hire' ? 'bg-green-600' :
                          fb.recommendation === 'hire' ? 'bg-green-500' :
                          fb.recommendation === 'no_hire' ? 'bg-red-500' : 'bg-red-600'
                        }`}>
                          {fb.recommendation.replace('_', ' ').toUpperCase()}
                        </span>
                      </div>
                      {fb.overallComments && (
                        <p className="text-sm text-gray-600">{fb.overallComments}</p>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-wrap gap-2 pt-4 border-t border-gray-300">
                {selectedApp.currentStage === 'applied' && (
                  <Button 
                    onClick={() => handleStageChange(selectedApp.id, 'screening')}
                    className="bg-yellow-600 hover:bg-yellow-700"
                    data-testid="move-to-screening"
                  >
                    Move to Screening
                  </Button>
                )}
                {selectedApp.currentStage === 'screening' && (
                  <Button 
                    onClick={() => { setShowScheduleModal(true); }}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="schedule-interview"
                  >
                    <Calendar className="w-4 h-4 mr-2" />
                    Schedule Interview
                  </Button>
                )}
                {(selectedApp.currentStage === 'scheduled' || selectedApp.currentStage === 'interviewed') && (
                  <>
                    <Button 
                      onClick={() => handleStageChange(selectedApp.id, 'interviewed')}
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Mark Interview Done
                    </Button>
                    <Button 
                      onClick={() => setShowFeedbackModal(true)}
                      className="bg-indigo-600 hover:bg-indigo-700"
                      data-testid="add-feedback"
                    >
                      <Star className="w-4 h-4 mr-2" />
                      Add Feedback
                    </Button>
                  </>
                )}
                {selectedApp.currentStage === 'feedback' && (
                  <Button 
                    onClick={() => setShowOfferModal(true)}
                    className="bg-green-600 hover:bg-green-700"
                    data-testid="make-offer"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Make Offer
                  </Button>
                )}
                {selectedApp.currentStage === 'offer' && (
                  <Button 
                    onClick={() => handleHire(selectedApp.id)}
                    className="bg-emerald-600 hover:bg-emerald-700"
                    data-testid="hire-candidate"
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Mark as Hired
                  </Button>
                )}
                {!['hired', 'rejected'].includes(selectedApp.currentStage) && (
                  <Button 
                    variant="outline"
                    onClick={() => handleReject(selectedApp.id)}
                    className="border-red-500 text-red-400 hover:bg-red-500/10"
                    data-testid="reject-candidate"
                  >
                    <XCircle className="w-4 h-4 mr-2" />
                    Reject
                  </Button>
                )}
                
                {/* Withdraw/Reassign for recruiter-assigned candidates */}
                {selectedApp.applicationSource === 'recruiter_assigned' && !selectedApp.isWithdrawn && (
                  <Button 
                    variant="outline"
                    onClick={() => handleWithdraw(selectedApp.id)}
                    className="border-orange-500 text-orange-500 hover:bg-orange-500/10"
                    data-testid="withdraw-from-hr"
                  >
                    <UserMinus className="w-4 h-4 mr-2" />
                    Withdraw from HR
                  </Button>
                )}
                {selectedApp.isWithdrawn && (
                  <Button 
                    onClick={() => handleReassign(selectedApp.id)}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="reassign-to-hr"
                  >
                    <UserPlus className="w-4 h-4 mr-2" />
                    Re-assign to HR
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Schedule Interview Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Schedule Interview
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleScheduleInterview} className="space-y-4">
            <div>
              <Label className="text-gray-600">Date & Time *</Label>
              <Input
                type="datetime-local"
                required
                value={scheduleForm.scheduledAt}
                onChange={(e) => setScheduleForm({...scheduleForm, scheduledAt: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
                data-testid="schedule-datetime"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-600">Duration (mins)</Label>
                <Input
                  type="number"
                  value={scheduleForm.duration}
                  onChange={(e) => setScheduleForm({...scheduleForm, duration: parseInt(e.target.value)})}
                  className="bg-gray-50 border-gray-300 text-gray-900"
                />
              </div>
              <div>
                <Label className="text-gray-600">Type</Label>
                <select
                  value={scheduleForm.interviewType}
                  onChange={(e) => setScheduleForm({...scheduleForm, interviewType: e.target.value})}
                  className="w-full h-10 px-3 bg-gray-100 border border-gray-300 rounded-md text-gray-900"
                >
                  <option value="video">Video Call</option>
                  <option value="phone">Phone</option>
                  <option value="in-person">In-Person</option>
                </select>
              </div>
            </div>
            <div>
              <Label className="text-gray-600">Meeting Link / Location</Label>
              <Input
                value={scheduleForm.location}
                onChange={(e) => setScheduleForm({...scheduleForm, location: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
                placeholder="https://meet.google.com/..."
              />
            </div>
            <div>
              <Label className="text-gray-600">Interviewer Name *</Label>
              <Input
                required
                value={scheduleForm.interviewerName}
                onChange={(e) => setScheduleForm({...scheduleForm, interviewerName: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
              />
            </div>
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-blue-600 hover:bg-blue-700"
              data-testid="submit-schedule"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Schedule Interview
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Feedback Modal */}
      <Dialog open={showFeedbackModal} onOpenChange={setShowFeedbackModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <Star className="w-5 h-5" />
              Submit Feedback
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmitFeedback} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-600">Overall Rating</Label>
                <select
                  value={feedbackForm.rating}
                  onChange={(e) => setFeedbackForm({...feedbackForm, rating: parseInt(e.target.value)})}
                  className="w-full h-10 px-3 bg-gray-100 border border-gray-300 rounded-md text-gray-900"
                  data-testid="feedback-rating"
                >
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n} Star{n > 1 ? 's' : ''}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-600">Recommendation</Label>
                <select
                  value={feedbackForm.recommendation}
                  onChange={(e) => setFeedbackForm({...feedbackForm, recommendation: e.target.value})}
                  className="w-full h-10 px-3 bg-gray-100 border border-gray-300 rounded-md text-gray-900"
                  data-testid="feedback-recommendation"
                >
                  <option value="strong_hire">Strong Hire</option>
                  <option value="hire">Hire</option>
                  <option value="no_hire">No Hire</option>
                  <option value="strong_no_hire">Strong No Hire</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-2">
              <div>
                <Label className="text-gray-600 text-xs">Technical</Label>
                <select
                  value={feedbackForm.technicalSkills}
                  onChange={(e) => setFeedbackForm({...feedbackForm, technicalSkills: parseInt(e.target.value)})}
                  className="w-full h-9 px-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900 text-sm"
                >
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-600 text-xs">Communication</Label>
                <select
                  value={feedbackForm.communicationSkills}
                  onChange={(e) => setFeedbackForm({...feedbackForm, communicationSkills: parseInt(e.target.value)})}
                  className="w-full h-9 px-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900 text-sm"
                >
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-gray-600 text-xs">Culture Fit</Label>
                <select
                  value={feedbackForm.cultureFit}
                  onChange={(e) => setFeedbackForm({...feedbackForm, cultureFit: parseInt(e.target.value)})}
                  className="w-full h-9 px-2 bg-gray-100 border border-gray-300 rounded-md text-gray-900 text-sm"
                >
                  {[1,2,3,4,5].map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
            </div>
            <div>
              <Label className="text-gray-600">Comments</Label>
              <Textarea
                rows={3}
                value={feedbackForm.overallComments}
                onChange={(e) => setFeedbackForm({...feedbackForm, overallComments: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
                placeholder="Overall interview feedback..."
                data-testid="feedback-comments"
              />
            </div>
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="submit-feedback"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Feedback
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Offer Modal */}
      <Dialog open={showOfferModal} onOpenChange={setShowOfferModal}>
        <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-gray-900 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" />
              Make Offer
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleMakeOffer} className="space-y-4">
            <div>
              <Label className="text-gray-600">Salary Package *</Label>
              <Input
                required
                value={offerForm.salary}
                onChange={(e) => setOfferForm({...offerForm, salary: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
                placeholder="e.g., 15 LPA"
                data-testid="offer-salary"
              />
            </div>
            <div>
              <Label className="text-gray-600">Joining Date *</Label>
              <Input
                type="date"
                required
                value={offerForm.joiningDate}
                onChange={(e) => setOfferForm({...offerForm, joiningDate: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
                data-testid="offer-joining-date"
              />
            </div>
            <div>
              <Label className="text-gray-600">Designation</Label>
              <Input
                value={offerForm.designation}
                onChange={(e) => setOfferForm({...offerForm, designation: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
              />
            </div>
            <div>
              <Label className="text-gray-600">Benefits</Label>
              <Textarea
                rows={2}
                value={offerForm.benefits}
                onChange={(e) => setOfferForm({...offerForm, benefits: e.target.value})}
                className="bg-gray-50 border-gray-300 text-gray-900"
                placeholder="Health insurance, Stock options..."
              />
            </div>
            <Button 
              type="submit" 
              disabled={submitting}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="submit-offer"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Send Offer
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ApplicantTracking;
